/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isEmpty from 'lodash.isempty';
import {Device} from '@ion/core/src/types/entities';
import {useSmartSearchDevice} from '@ion/query/src/hooks/useSmartSearchDevice';

export interface UseDeviceRecordParams {
  deviceId?: number | string;
  fallbacks?: (Device | undefined)[];
}

export type UseDeviceRecordResult = {
  device: Device;
  isLoading: boolean;
  isError: boolean;
  refresh: () => void;
};

const EMPTY_DEVICE = {} as Device;

const NO_FALLBACKS: (Device | undefined)[] = [];

const firstPopulated = (devices: (Device | undefined)[]): Device => (
  devices?.find((item) => !isEmpty(item)) || EMPTY_DEVICE
);

/**
 * The useDeviceRecord hook is the single seam every Device Details field reads
 * its device from, so a field never depends on which grid columns a parent
 * happened to load. Consumers derive their own values from the device it
 * returns rather than issuing a device query of their own.
 *
 * The only gate is a data gate: the underlying query runs once a `deviceId` is
 * available. Permissions are deliberately not an input here; the hook answers
 * what the current device is, and the caller separately decides whether an
 * action is allowed.
 *
 * `fallbacks` are placeholders only, tried in order while the query is in
 * flight and discarded the moment it resolves. The fetched device is always
 * authoritative once present, even for fields it leaves empty.
 *
 * ```typescript
 * import {useDeviceRecord} from './useDeviceRecord';
 *  ...
 *  const {device} = useDeviceRecord({deviceId, fallbacks: [deviceDetail]});
 * ```
 *
 * @param deviceId  of the device to read
 * @param fallbacks already-loaded devices used only while loading
 * @returns the resolved device, load state, and a refresh function
 */
export const useDeviceRecord = ({
  deviceId,
  fallbacks = NO_FALLBACKS
}: UseDeviceRecordParams): UseDeviceRecordResult => {
  const {
    device,
    refresh,
    isLoading,
    isError
  } = useSmartSearchDevice({id: deviceId}, !!deviceId);

  return {
    device  : isEmpty(device) ? firstPopulated(fallbacks) : device,
    isLoading,
    isError,
    refresh : refresh as () => void
  };
};

export default useDeviceRecord;
